import { supabase } from '../lib/supabase';
import { getVehicleLocations as getSamsaraLocations } from './samsaraService';
import { getELDVehicleLocations } from './eldService';

/**
 * Unified location service that automatically uses the configured provider
 * Falls back to ELD API if Samsara is not configured
 */

interface VehicleLocation {
  vehicleId: string;
  latitude: number;
  longitude: number;
  heading?: number;
  speed?: number;
  time: string;
}

interface LocationResponse {
  success: boolean;
  locations?: VehicleLocation[];
  error?: string;
  provider?: 'samsara' | 'eld' | 'none';
}

/**
 * Check which location provider is configured and enabled
 */
async function getActiveLocationProvider(): Promise<'samsara' | 'eld' | 'none'> {
  try {
    // Provider detection uses the status-only edge function (booleans, never
    // secrets). Its `eld` status already folds in the eld_connections table.
    const { data, error } = await supabase.functions.invoke('integration-status');
    if (error) throw error;

    const status = data?.status || {};
    const eldActive = !!(status.eld?.enabled && status.eld?.connected);
    const samsaraActive = !!(status.samsara?.enabled && status.samsara?.connected);

    // ELD is the primary provider; Samsara only when ELD isn't active.
    if (eldActive) return 'eld';
    if (samsaraActive) return 'samsara';

    // Default to ELD if nothing else is configured (matches prior behavior).
    return 'eld';
  } catch (error) {
    console.error('Error checking location provider:', error);
    return 'none';
  }
}

/**
 * Get vehicle locations from the active provider (Samsara or ELD)
 * Automatically falls back to ELD if Samsara is not configured
 */
export async function getVehicleLocations(): Promise<LocationResponse> {
  try {
    const provider = await getActiveLocationProvider();

    console.log(`[LocationService] Using provider: ${provider}`);

    if (provider === 'samsara') {
      console.log('[LocationService] Fetching locations from Samsara...');
      const result = await getSamsaraLocations();
      return {
        ...result,
        provider: 'samsara',
      };
    } else if (provider === 'eld') {
      console.log('[LocationService] Fetching locations from ELD...');
      const result = await getELDVehicleLocations();
      return {
        ...result,
        provider: 'eld',
      };
    } else {
      console.warn('[LocationService] No location provider configured');
      return {
        success: false,
        error: 'No location tracking provider configured. Please configure Samsara or ELD in Settings > Integrations.',
        provider: 'none',
      };
    }
  } catch (error: any) {
    console.error('[LocationService] Error fetching vehicle locations:', error);
    return {
      success: false,
      error: error.message || 'Failed to fetch vehicle locations',
      provider: 'none',
    };
  }
}

/**
 * Get the name of the active location provider
 */
export async function getActiveProviderName(): Promise<string> {
  const provider = await getActiveLocationProvider();
  
  switch (provider) {
    case 'samsara':
      return 'Samsara GPS';
    case 'eld':
      return 'ELD Integration';
    default:
      return 'None';
  }
}

/**
 * Check if any location provider is configured
 */
export async function isLocationTrackingAvailable(): Promise<boolean> {
  const provider = await getActiveLocationProvider();
  return provider !== 'none';
}
